import React, { useState, useRef, useEffect } from "react";
import { UncontrolledTooltip } from "reactstrap";
import ErrorDialog from "./ErrorDialog";

const Cell = ({
  rowId,
  rowType,
  column,
  status,
  customStyles = {},
  className = "",
  inline = false,
  tableManager,
  children,
  noBorder = false,
  isTemp = false,
  disableAddModuleCell = false,
  errorMessage = "",
  tooltip = null,
  isFillerRow = false,
  sectionId = null,
  selectionManager,
  showClearOverrideTooltip = false,
  blurred = false,
  copyPasteTooltip = {},
  hasError = false,
  onContextMenu = null,
  hoverComponent = null,
  hasExpo = false,
  hasFiller = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const cellRef = useRef(null);
  const [cellWidth, setCellWidth] = useState(0);

  const isFocused = tableManager.isFocused(rowType, rowId, column);
  const isActive = tableManager.isActive(rowType, rowId, column);
  const currentStatus = isFocused ? "focussed" : isActive ? "active" : "idle";
  const shouldHighlight = isFocused || (inline && isActive);
  const appliedStyles = {
    ...(shouldHighlight && {
      outline: "2px solid #2C92E6",
      boxShadow: "0px 0px 10px 0px #AAAAAA",
      zIndex: 1,
    }),
    ...((errorMessage || hasError) && {
      outline: "2px solid #ff0000",
      boxShadow: "0px 0px 10px 0px #AAAAAA",
      zIndex: 1,
      backgroundColor: "rgba(255, 0, 0, 0.1)",
    }),
    ...(blurred && { backgroundColor: "#DDD" }),
    ...customStyles,
  };

  useEffect(() => {
    if (cellRef.current) {
      setCellWidth(cellRef.current.offsetWidth);
    }
  }, [errorMessage]);

  const reloadItem = () => {
    tableManager.reloadItem(rowType, rowId, column);
  };

  const effectiveClassName = `cell position-relative column-gap-3 d-flex align-items-center flex-shrink-0 ${className} ${
    !noBorder && "border-end"
  } ${!inline && "ps-2 py-2"} status-${currentStatus} ${
    tableManager.isHighlighted(rowType, rowId, column, "highlight") &&
    "highlighted"
  }`;

  const LoaderDiv = () => {
    return (
      <div className="w-100 mx-2 py-2 my-2 h-10px loader-grey rounded-5"></div>
    );
  };

  return (
    <>
      <div
        ref={cellRef}
        className={effectiveClassName}
        onClick={() => {
          if (isFillerRow)
            tableManager.highlight(sectionId, "section", "filler");
          else if (column == "add_module")
            tableManager.set(rowId, rowType, column, "active");
          else if (
            rowType === "section" &&
            column === "filler" &&
            selectionManager.findGroup(rowId).modules.length === 0
          )
            return;
          else if (!isTemp && !disableAddModuleCell) {
            if (!tableManager.isActive(rowType, rowId, column)) {
              tableManager.set(rowId, rowType, column, "focus");
            }
          }
        }}
        onContextMenu={onContextMenu}
        style={appliedStyles}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title={tooltip}
        id={`cell_${rowType}_${rowId}_${column}`}
        onDoubleClick={() => {
          if (tableManager.isCurrentlyFocused()) {
            tableManager.set(rowId, rowType, column, "active");
          }
        }}
      >
        {errorMessage && isHovered && (
          <ErrorDialog
            body={errorMessage}
            header={"Error"}
            cellWidth={cellWidth}
            reloadItem={reloadItem}
          ></ErrorDialog>
        )}
        {tableManager.isCopiedCell(rowId, rowType, column) && (
          <div
            className={`position-absolute top-50 start-50 translate-middle bg-dark text-white px-2 py-1 rounded small opacity-75`}
            style={{ zIndex: 1000 }}
          >
            Copied!
          </div>
        )}
        {isTemp ? <LoaderDiv /> : children}
        {isHovered && hoverComponent && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              background: "#fff",
              border: "1px solid #ccc",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              zIndex: 999999,
              padding: "6px",
              minWidth: "150px",
              whiteSpace: "nowrap",
            }}
          >
            {hoverComponent}
          </div>
        )}
        {hasExpo && (
          <div
            className="fs-10px d-flex text-muted me-2"
            style={{ textWrap: "nowrap" }}
          >
            No Expo
          </div>
        )}
        {hasFiller && (
          <div
            className="fs-10px d-flex text-muted me-4"
            style={{ textWrap: "nowrap" }}
          >
            No Fillers
          </div>
        )}
      </div>
      {isFillerRow &&
        document.getElementById(`cell_${rowType}_${rowId}_${column}`) && (
          <UncontrolledTooltip
            target={`cell_${rowType}_${rowId}_${column}`}
            placement="bottom"
          >
            Refer Fillers. Click to highlight.
          </UncontrolledTooltip>
        )}

      {showClearOverrideTooltip && !isFillerRow && (
        <UncontrolledTooltip
          target={`cell_${rowType}_${rowId}_${column}`}
          placement="top"
        >
          {tableManager.isActive(rowType, rowId, column)
            ? column === "width"
              ? "Clear the value to use equally distribute the width"
              : `Clear the value to use the section ${column}`
            : `${column[0].toUpperCase() + column.slice(1)} overridden`}
        </UncontrolledTooltip>
      )}

      {rowType === "section" &&
        column === "filler" &&
        selectionManager.findGroup(rowId).modules.length === 0 && (
          <UncontrolledTooltip
            target={`cell_${rowType}_${rowId}_${column}`}
            placement="top"
          >
            Atleast one module required to add fillers
          </UncontrolledTooltip>
        )}
      {copyPasteTooltip.show && (
        <UncontrolledTooltip
          placement="top"
          isOpen={copyPasteTooltip.show}
          target={copyPasteTooltip.target}
        >
          {copyPasteTooltip.message}
        </UncontrolledTooltip>
      )}
    </>
  );
};

export default Cell;
